import Resources from './CorporateViewTabs/Resources';
import Notifications from './CorporateViewTabs/Notifications';
// import Incidents from './CorporateViewTabs/Incidents';
import AORs from './CorporateViewTabs/AorsTab/AORs';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import ReportsView from 'components/Reports/ReportsView';
import classNames from 'classnames';
import { LayersView } from './CorporateViewTabs/LayersTab/LayersView';
import OrgActionsPage from 'components/IAP/OrgActionsPage';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useUscgMapContext } from './CorporateViewTabs/LayersTab/UscgMapContext';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MaterialSymbols } from 'material-design-icons-literal-types';
import { useHighSecurityFlag } from 'utils/featureFlags';
import Incidents from './CorporateViewTabs/IncidentsTab/Incidents';

export enum CorporateViewTabs {
  Notifications = 'Notifications',
  Reports = 'Reports',
  Incidents = 'Incidents',
  Actions = 'Actions',
  AORs = 'AORs',
  Resources = 'Resources',
  Layers = 'Layers',
}

export const TabNameToIconName: Record<CorporateViewTabs, MaterialSymbols> = {
  [CorporateViewTabs.Notifications]: 'notifications',
  [CorporateViewTabs.Reports]: 'docs',
  [CorporateViewTabs.Incidents]: 'warning_amber',
  [CorporateViewTabs.Actions]: 'checklist',
  [CorporateViewTabs.AORs]: 'location_on',
  [CorporateViewTabs.Resources]: 'package_2',
  [CorporateViewTabs.Layers]: 'stacks',
  // [CorporateViewTabs.ICS]: 'warning',
};

type CorporateViewTabGroupProps = {
  ics?: boolean;
  currentMode: string;
  updateMode: (isFutureMode: string) => void;
  currentPhase: number;
};

export const CorporateViewTabGroup = ({ ics }: CorporateViewTabGroupProps) => {
  const highSecurityFlag = useHighSecurityFlag();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = useMemo(
    () =>
      (searchParams.get('openTab') as CorporateViewTabs) ||
      CorporateViewTabs.Notifications,
    [searchParams]
  );
  const setCurrentTab = useCallback(
    (newCurrentTab: CorporateViewTabs) => {
      setSearchParams(
        (searchParams) => {
          searchParams.set('openTab', newCurrentTab);
          return searchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { isMapVisible } = useUscgMapContext();

  const tabToView: Record<CorporateViewTabs, React.ReactNode> = {
    Notifications: <Notifications />,
    Reports: <ReportsView />,
    Incidents: <Incidents />,
    Actions: <OrgActionsPage />,
    AORs: <AORs />,
    Resources: <Resources />,
    Layers: <LayersView />,
  };

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ borderBottom: '2px solid var(--text-weak)' }}
      >
        <div
          className="initial-response-stepper w-100"
          style={{ borderTop: 'none', borderBottom: 'none' }}
        >
          <div className="d-flex align-items-center justify-content-center flex-wrap gap-2">
            {Object.keys(CorporateViewTabs).map((tabName, index) => {
              if (tabName === 'ICS' && !ics) {
                return null;
              }
              if (tabName === 'AORs' && ics) {
                return null;
              }
              if (tabName === 'Actions' && highSecurityFlag) {
                return null;
              }
              return (
                <OverlayTrigger
                  key={index}
                  placement="bottom"
                  overlay={(props) => <Tooltip {...props}>{tabName}</Tooltip>}
                >
                  <StylishNewButton
                    key={index}
                    className={
                      'button--secondary tab' +
                      (tabName === currentTab ? ' active' : '')
                    }
                    onClick={() => setCurrentTab(tabName as CorporateViewTabs)}
                  >
                    <SharedIcon
                      iconName={TabNameToIconName[tabName as CorporateViewTabs]}
                      size="sm"
                    />
                  </StylishNewButton>
                </OverlayTrigger>
              );
            })}
          </div>
        </div>
      </div>
      <div
        className={classNames('p-2', {
          'tw:min-h-0 tw:flex-auto tw:overflow-y-auto': isMapVisible,
        })}
      >
        {tabToView[currentTab]}
      </div>
    </>
  );
};
